import { z } from "zod";

import { leadSourceSchema } from "./domain";

export const STATISTICS_PRESETS = ["month", "week", "custom", "all"] as const;
export const STATISTICS_EVOLUTION_MEDIA_TYPE = "application/vnd.polaris.statistics-evolution+json";
export const STATISTICS_RETENTION_MEDIA_TYPE = "application/vnd.polaris.statistics-retention+json";
/** Evolution plus `replies` (T-PL-40): who answered us and who never did. */
export const STATISTICS_REPLIES_MEDIA_TYPE = "application/vnd.polaris.statistics-replies+json";
/** Replies plus `insights` (T-PL-40): weekly money, customers, campaigns and operators. */
export const STATISTICS_INSIGHTS_MEDIA_TYPE = "application/vnd.polaris.statistics-insights+json";
/** Insights plus `timing` (T-PL-40 phase 4): speed of our first contact and arrival hours. */
export const STATISTICS_TIMING_MEDIA_TYPE = "application/vnd.polaris.statistics-timing+json";
/** Timing plus `channels` (T-PL-43 Fase 3): the main numbers side by side for each source. */
export const STATISTICS_CHANNELS_MEDIA_TYPE = "application/vnd.polaris.statistics-channels+json";
/** Upper bounds, in minutes, of the speed bands of our first contact; the last is open. */
export const STATISTICS_CONTACT_SPEED_BANDS = [5, 30, 120, 1440] as const;
/** Working hours for «fuori orario»: Monday to Friday, from 9 to 19 in the workspace zone. */
export const STATISTICS_WORKING_HOURS = { from: 9, to: 19 } as const;
/** A contacted lead is "never replied" only after these days without an answer. */
export const STATISTICS_REPLY_WAIT_DAYS = 3;
export const statisticsDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, "Data non valida.");
export const statisticsQuerySchema = z
  .object({
    preset: z.enum(STATISTICS_PRESETS).default("month"),
    from: statisticsDateSchema.optional(),
    to: statisticsDateSchema.optional(),
    operatorId: z.string().min(1).max(100).optional(),
    source: leadSourceSchema.optional(),
    accountId: z.string().min(1).max(100).optional(),
    campaignId: z.string().min(1).max(100).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.preset === "custom" && (!value.from || !value.to)) {
      ctx.addIssue({ code: "custom", message: "Scegli entrambe le date.", path: ["from"] });
    }
    if (value.from && value.to && value.from > value.to) {
      ctx.addIssue({
        code: "custom",
        message: "La data finale precede quella iniziale.",
        path: ["to"],
      });
    }
  });
export type StatisticsQuery = z.infer<typeof statisticsQuerySchema>;

export const statisticsMetricSchema = z
  .object({
    value: z.number().finite().nullable(),
    reason: z.string().nullable(),
    sample: z.number().finite().nonnegative().nullable(),
  })
  .strict();
export type StatisticsMetric = z.infer<typeof statisticsMetricSchema>;
export const statisticsCoverageSchema = z
  .object({
    source: z.string(),
    state: z.enum(["complete", "partial", "unavailable"]),
    from: z.string().nullable(),
    to: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
    notes: z.array(z.string()),
  })
  .strict();
export type StatisticsCoverage = z.infer<typeof statisticsCoverageSchema>;

export const statisticsRetentionSchema = z
  .object({
    asOf: z.string().nullable(),
    acquired: statisticsMetricSchema,
    active: statisticsMetricSchema,
    ceased: statisticsMetricSchema,
    overdue: statisticsMetricSchema,
    unknown: statisticsMetricSchema,
    ceasedShare: statisticsMetricSchema,
    ceasedInPeriod: statisticsMetricSchema,
    customers: z.array(
      z
        .object({
          id: z.string(),
          leadId: z.string().nullable(),
          name: z.string(),
          status: z.enum(["active", "ceased", "overdue", "unknown"]),
          firstPaidAt: z.string(),
          endedAt: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type StatisticsRetention = z.infer<typeof statisticsRetentionSchema>;

const advertisingSchema = z
  .object({
    accountId: z.string(),
    accountName: z.string(),
    timezone: z.string(),
    currency: z.string(),
    spend: statisticsMetricSchema,
    leads: statisticsMetricSchema,
    cpl: statisticsMetricSchema,
    historicalCpl: statisticsMetricSchema,
    crmLeads: statisticsMetricSchema,
    payingCustomers: statisticsMetricSchema,
    cac: statisticsMetricSchema,
    coverage: statisticsCoverageSchema,
  })
  .strict();
const evolutionPointSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    incoming: z.number().nonnegative(),
    closures: z.number().nonnegative().nullable(),
    conversionRate: z.number().nullable(),
    received: z.array(z.object({ currency: z.string(), value: z.number().nullable() }).strict()),
    advertising: z.array(
      z
        .object({ currency: z.string(), spend: z.number().nullable(), cpl: z.number().nullable() })
        .strict(),
    ),
  })
  .strict();
const comparisonMetricsSchema = z
  .object({
    incoming: statisticsMetricSchema,
    closures: statisticsMetricSchema,
    conversionRate: statisticsMetricSchema,
    firstResponseMinutes: statisticsMetricSchema,
    medianCloseDays: statisticsMetricSchema,
  })
  .strict();
export const statisticsEvolutionSchema = z
  .object({
    bucketDays: z.number().int().positive(),
    points: z.array(evolutionPointSchema),
    previous: z
      .object({
        from: z.string(),
        to: z.string(),
        metrics: comparisonMetricsSchema,
        received: z.array(
          z.object({ currency: z.string(), metric: statisticsMetricSchema }).strict(),
        ),
        advertising: z.array(advertisingSchema),
        points: z.array(evolutionPointSchema),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type StatisticsEvolution = z.infer<typeof statisticsEvolutionSchema>;

const replyCountsSchema = z
  .object({
    replied: z.number().int().nonnegative(),
    neverReplied: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    notContacted: z.number().int().nonnegative(),
  })
  .strict();
/**
 * Leads of the period by what happened after our first message or WhatsApp call: they wrote
 * back (or took a call), never did after the waiting days, are still within them, or were never
 * contacted at all. Funnel steps are counted on the same leads; customers are `cohortCustomers`.
 * The rates are over the leads contacted at least `waitDays` ago (their `sample`), answered or
 * not: an early answer of a recent lead does not count before the others of its days can.
 */
export const statisticsRepliesSchema = z
  .object({
    waitDays: z.number().int().positive(),
    contacted: statisticsMetricSchema,
    replied: statisticsMetricSchema,
    neverReplied: statisticsMetricSchema,
    waiting: statisticsMetricSchema,
    notContacted: statisticsMetricSchema,
    replyRate: statisticsMetricSchema,
    neverRepliedRate: statisticsMetricSchema,
    sites: statisticsMetricSchema,
    lost: statisticsMetricSchema,
    unqualified: statisticsMetricSchema,
    /** Current open leads, whatever their arrival: work for today. */
    openNotContacted: statisticsMetricSchema,
    openNeverReplied: statisticsMetricSchema,
    operators: z.array(
      z
        .object({
          id: z.string().nullable(),
          replyRate: statisticsMetricSchema,
          neverReplied: statisticsMetricSchema,
        })
        .strict(),
    ),
    points: z.array(
      z.object({ from: z.string(), to: z.string() }).merge(replyCountsSchema).strict(),
    ),
    previous: z
      .object({ replyRate: statisticsMetricSchema, neverRepliedRate: statisticsMetricSchema })
      .strict()
      .nullable(),
  })
  .strict();
export type StatisticsReplies = z.infer<typeof statisticsRepliesSchema>;

const bucketSchema = z.object({ from: z.string(), to: z.string() });
/**
 * What the money tab and the operator cards draw (T-PL-40). Buckets follow `evolution.points`.
 * Ads customers: customers from Meta campaigns with their first payment in the bucket, so the
 * cost per customer of a recent week is not lowered by leads that have not paid yet. Monthly
 * recurring amounts use today's prices: Stripe keeps no price history.
 */
export const statisticsInsightsSchema = z
  .object({
    operators: z.array(
      z
        .object({
          id: z.string().nullable(),
          points: z.array(
            bucketSchema
              .extend({
                incoming: z.number().int().nonnegative(),
                replied: z.number().int().nonnegative(),
                neverReplied: z.number().int().nonnegative(),
                closures: z.number().int().nonnegative().nullable(),
              })
              .strict(),
          ),
        })
        .strict(),
    ),
    money: z.array(
      z
        .object({
          currency: z.string(),
          adsCustomers: statisticsMetricSchema,
          cac: statisticsMetricSchema,
          mrrNew: statisticsMetricSchema,
          mrrLost: statisticsMetricSchema,
          previous: z
            .object({ cac: statisticsMetricSchema, mrrNew: statisticsMetricSchema })
            .strict()
            .nullable(),
          points: z.array(
            bucketSchema
              .extend({
                adsCustomers: z.number().int().nonnegative().nullable(),
                mrrNew: z.number().nullable(),
                mrrLost: z.number().nullable(),
              })
              .strict(),
          ),
        })
        .strict(),
    ),
    customers: z
      .object({
        gained: statisticsMetricSchema,
        lost: statisticsMetricSchema,
        /** Customers left out of the churn: their subscription history is not proven. */
        unverified: statisticsMetricSchema,
        points: z.array(
          bucketSchema
            .extend({
              gained: z.number().int().nonnegative().nullable(),
              lost: z.number().int().nonnegative().nullable(),
            })
            .strict(),
        ),
      })
      .strict(),
    campaigns: z.array(
      z
        .object({
          accountId: z.string(),
          campaignId: z.string(),
          replied: statisticsMetricSchema,
          neverReplied: statisticsMetricSchema,
          costPerReply: statisticsMetricSchema,
          customers: statisticsMetricSchema,
          cac: statisticsMetricSchema,
          points: z.array(bucketSchema.extend({ spend: z.number().nullable() }).strict()),
        })
        .strict(),
    ),
  })
  .strict();
export type StatisticsInsights = z.infer<typeof statisticsInsightsSchema>;

const timingCountsSchema = z
  .object({
    leads: z.number().int().nonnegative(),
    /** Like `neverReplied`, only leads contacted at least the waiting days ago. */
    replied: z.number().int().nonnegative(),
    neverReplied: z.number().int().nonnegative(),
  })
  .strict();
/**
 * When the leads of the period arrive and how fast we reach them (T-PL-40 phase 4), read like
 * `replies`: as they are today, in the workspace zone.
 */
export const statisticsTimingSchema = z
  .object({
    /** One row per band of `STATISTICS_CONTACT_SPEED_BANDS`, plus the open last one. */
    speed: z.array(
      timingCountsSchema.extend({ upToMinutes: z.number().int().positive().nullable() }).strict(),
    ),
    /** Weekday 0 = Monday. Only the hours with at least one lead. */
    arrivals: z.array(
      timingCountsSchema
        .extend({
          weekday: z.number().int().min(0).max(6),
          hour: z.number().int().min(0).max(23),
        })
        .strict(),
    ),
    outsideHours: statisticsMetricSchema,
  })
  .strict();
export type StatisticsTiming = z.infer<typeof statisticsTimingSchema>;

/**
 * One source of the leads (WhatsApp, Instagram, Messenger, Meta Lead Ads, …): the
 * numbers the page shows when that source is chosen in «Fonte», side by side.
 */
export const statisticsChannelSchema = z
  .object({
    source: leadSourceSchema,
    incoming: statisticsMetricSchema,
    replyRate: statisticsMetricSchema,
    customers: statisticsMetricSchema,
    conversionRate: statisticsMetricSchema,
  })
  .strict();
export type StatisticsChannel = z.infer<typeof statisticsChannelSchema>;

export const statisticsDtoSchema = z
  .object({
    range: z
      .object({
        preset: z.enum(STATISTICS_PRESETS),
        from: z.string(),
        to: z.string(),
        timezone: z.string(),
        asOf: z.string(),
        trendGranularity: z.enum(["day", "week", "month"]),
      })
      .strict(),
    metrics: z
      .object({
        incoming: statisticsMetricSchema,
        closures: statisticsMetricSchema,
        cohortCustomers: statisticsMetricSchema,
        conversionRate: statisticsMetricSchema,
        open: statisticsMetricSchema,
        unassigned: statisticsMetricSchema,
        staleLeads: statisticsMetricSchema,
        firstResponseMinutes: statisticsMetricSchema,
        medianCloseDays: statisticsMetricSchema,
      })
      .strict(),
    advertising: z.array(advertisingSchema),
    campaigns: z.array(advertisingSchema.extend({ id: z.string(), name: z.string() })),
    economics: z.array(
      z
        .object({
          currency: z.string(),
          received: statisticsMetricSchema,
          refunds: statisticsMetricSchema,
          netReceived: statisticsMetricSchema,
          ltvObserved: statisticsMetricSchema,
          mrr: statisticsMetricSchema,
        })
        .strict(),
    ),
    customerMetrics: z
      .object({
        cohortCustomers: statisticsMetricSchema,
        activeStart: statisticsMetricSchema,
        churned: statisticsMetricSchema,
        churnRate: statisticsMetricSchema,
        reactivated: statisticsMetricSchema,
        ltvCohortAgeDays: statisticsMetricSchema,
      })
      .strict(),
    retention: statisticsRetentionSchema.optional(),
    evolution: statisticsEvolutionSchema.optional(),
    replies: statisticsRepliesSchema.optional(),
    insights: statisticsInsightsSchema.optional(),
    timing: statisticsTimingSchema.optional(),
    channels: z.array(statisticsChannelSchema).optional(),
    operators: z.array(
      z
        .object({
          id: z.string().nullable(),
          name: z.string(),
          incoming: statisticsMetricSchema,
          assigned: statisticsMetricSchema,
          closures: statisticsMetricSchema,
          cohortCustomers: statisticsMetricSchema,
          conversionRate: statisticsMetricSchema,
          firstResponseMinutes: statisticsMetricSchema,
          medianCloseDays: statisticsMetricSchema,
          attribution: z.enum(["observed", "inferred", "unavailable"]),
        })
        .strict(),
    ),
    trend: z.array(
      z
        .object({
          date: z.string(),
          leads: z.number().int().nonnegative(),
          closures: z.number().int().nonnegative(),
        })
        .strict(),
    ),
    technicalCosts: z.array(
      z
        .object({
          currency: z.string(),
          provider: z.string(),
          cost: statisticsMetricSchema,
          pending: z.number().int().nonnegative(),
        })
        .strict(),
    ),
    coverage: z.array(statisticsCoverageSchema),
    filters: z
      .object({
        operators: z.array(
          z.object({ id: z.string(), name: z.string(), active: z.boolean() }).strict(),
        ),
      })
      .strict(),
  })
  .strict();
export type StatisticsDto = z.infer<typeof statisticsDtoSchema>;

/** Invalid URL state falls back to the default range rather than issuing an invalid request. */
export function validateStatisticsSearch(raw: Record<string, unknown>): StatisticsQuery {
  const values = Object.fromEntries(
    Object.entries(raw).filter(
      ([key, value]) =>
        ["preset", "from", "to", "operatorId", "source", "accountId", "campaignId"].includes(key) &&
        typeof value === "string" &&
        value.length > 0,
    ),
  );
  const parsed = statisticsQuerySchema.safeParse(values);
  return parsed.success ? parsed.data : { preset: "month" };
}
